import React, { useState, useEffect } from "react";
import { db } from "../firebase-config";
import { collection, addDoc, updateDoc, deleteDoc, doc, getDocs, query, orderBy, serverTimestamp } from "firebase/firestore";

const regions = [
  "Budapest", "Pest megye", "Fejér megye", "Komárom-Esztergom megye", "Veszprém megye",
  "Győr-Moson-Sopron megye", "Vas megye", "Zala megye", "Baranya megye", "Somogy megye",
  "Tolna megye", "Borsod-Abaúj-Zemplén megye", "Heves megye", "Nógrád megye",
  "Hajdú-Bihar megye", "Jász-Nagykun-Szolnok megye", "Szabolcs-Szatmár-Bereg megye",
  "Bács-Kiskun megye", "Békés megye", "Csongrád-Csanád megye"
];

const structures = [
  { id: "fa", name: "Fa szerkezet" },
  { id: "blokk", name: "Blokk" },
  { id: "tegla", name: "Tégla" }
];

export default function PartnerManagement() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [formData, setFormData] = useState({
    companyName: "",
    contactName: "",
    email: "",
    phone: "",
    regions: [],
    structures: [],
    capacity: "medium",
    commissionRate: 5,
    notes: "",
    status: "active"
  });

  useEffect(() => {
    fetchPartners();
  }, []);

  const fetchPartners = async () => {
    try {
      const q = query(collection(db, "construction_partners"), orderBy("createdAt", "desc"));
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setPartners(data);
    } catch (error) {
      console.error("Error fetching partners:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPartner) {
        await updateDoc(doc(db, "construction_partners", editingPartner.id), {
          ...formData,
          updatedAt: serverTimestamp()
        });
      } else {
        await addDoc(collection(db, "construction_partners"), {
          ...formData,
          assignedLeads: 0,
          completedProjects: 0,
          totalRevenue: 0,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      }
      fetchPartners();
      resetForm();
    } catch (error) {
      console.error("Error saving partner:", error);
    }
  };

  const handleDelete = async (partnerId) => {
    if (!window.confirm("Biztosan törölni szeretnéd ezt a partnert?")) return;
    try {
      await deleteDoc(doc(db, "construction_partners", partnerId));
      fetchPartners();
    } catch (error) {
      console.error("Error deleting partner:", error);
    }
  };

  const resetForm = () => {
    setFormData({
      companyName: "",
      contactName: "",
      email: "",
      phone: "",
      regions: [],
      structures: [],
      capacity: "medium",
      commissionRate: 5,
      notes: "",
      status: "active"
    });
    setEditingPartner(null);
    setShowAddModal(false);
  };

  const handleEdit = (partner) => {
    setFormData({
      companyName: partner.companyName || "",
      contactName: partner.contactName || "",
      email: partner.email || "",
      phone: partner.phone || "",
      regions: partner.regions || [],
      structures: partner.structures || [],
      capacity: partner.capacity || "medium",
      commissionRate: partner.commissionRate || 5,
      notes: partner.notes || "",
      status: partner.status || "active"
    });
    setEditingPartner(partner);
    setShowAddModal(true);
  };

  const toggleArrayItem = (array, item) => {
    if (array.includes(item)) {
      return array.filter(i => i !== item);
    }
    return [...array, item];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin h-8 w-8 border-2 border-secondary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white">Kivitelező Partnerek</h2>
          <p className="text-gray-400 text-sm">Belső partner adatbázis - NEM PUBLIKUS</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="bg-secondary hover:bg-hover text-white font-medium px-4 py-2 rounded-lg transition-all"
        >
          + Új partner
        </button>
      </div>

      {/* Warning */}
      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 flex items-start gap-3">
        <span className="text-yellow-500 text-xl">⚠️</span>
        <div>
          <p className="text-yellow-400 font-medium">Zárt partnermodell</p>
          <p className="text-gray-400 text-sm">
            Ezek az adatok SOHA nem jelennek meg publikusan. Az ügyfelek nem látják a partnereket.
          </p>
        </div>
      </div>

      {/* Partners Grid */}
      <div className="grid gap-4">
        {partners.length === 0 ? (
          <div className="bg-[#1a1a1a] rounded-xl p-8 text-center border border-white/5">
            <p className="text-gray-400">Még nincs hozzáadott partner</p>
          </div>
        ) : (
          partners.map(partner => (
            <div
              key={partner.id}
              className={`bg-[#1a1a1a] rounded-xl p-5 border transition-all ${
                partner.status === "active" ? "border-green-500/20" : "border-white/5 opacity-60"
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-bold text-white">{partner.companyName}</h3>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      partner.status === "active" 
                        ? "bg-green-500/20 text-green-400" 
                        : "bg-gray-500/20 text-gray-400"
                    }`}>
                      {partner.status === "active" ? "Aktív" : "Inaktív"}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      partner.capacity === "high" ? "bg-red-500/20 text-red-400" :
                      partner.capacity === "medium" ? "bg-yellow-500/20 text-yellow-400" :
                      "bg-green-500/20 text-green-400"
                    }`}>
                      {partner.capacity === "high" ? "Magas terheltség" :
                       partner.capacity === "medium" ? "Közepes terheltség" :
                       "Alacsony terheltség"}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-3">
                    <div>
                      <span className="text-gray-500">Kapcsolattartó:</span>
                      <p className="text-white">{partner.contactName}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Email:</span>
                      <p className="text-white">{partner.email}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Telefon:</span>
                      <p className="text-white">{partner.phone}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Jutalék:</span>
                      <p className="text-secondary font-bold">{partner.commissionRate}%</p>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 mb-3">
                    {partner.structures?.map(s => (
                      <span key={s} className="text-xs bg-secondary/20 text-secondary px-2 py-1 rounded">
                        {structures.find(st => st.id === s)?.name || s}
                      </span>
                    ))}
                  </div>

                  <div className="flex flex-wrap gap-1">
                    {partner.regions?.slice(0, 5).map(r => (
                      <span key={r} className="text-xs bg-white/5 text-gray-400 px-2 py-0.5 rounded">
                        {r}
                      </span>
                    ))}
                    {partner.regions?.length > 5 && (
                      <span className="text-xs text-gray-500">+{partner.regions.length - 5} további</span>
                    )}
                  </div>

                  {/* Stats */}
                  <div className="flex gap-6 mt-3 pt-3 border-t border-white/5 text-sm">
                    <div>
                      <span className="text-gray-500">Hozzárendelt leadek:</span>
                      <span className="text-white ml-2 font-medium">{partner.assignedLeads || 0}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Befejezett projektek:</span>
                      <span className="text-white ml-2 font-medium">{partner.completedProjects || 0}</span>
                    </div>
                  </div>
                </div>

                <div className="flex gap-2 ml-4">
                  <button
                    onClick={() => handleEdit(partner)}
                    className="p-2 bg-white/5 hover:bg-white/10 rounded-lg transition-all"
                    title="Szerkesztés"
                  >
                    ✏️
                  </button>
                  <button
                    onClick={() => handleDelete(partner.id)}
                    className="p-2 bg-red-500/10 hover:bg-red-500/20 rounded-lg transition-all text-red-400"
                    title="Törlés"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1a1a1a] rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-white/10">
            <h3 className="text-xl font-bold text-white mb-6">
              {editingPartner ? "Partner szerkesztése" : "Új partner hozzáadása"}
            </h3>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-300 text-sm mb-1">Cégnév *</label>
                  <input
                    type="text"
                    value={formData.companyName}
                    onChange={e => setFormData(prev => ({ ...prev, companyName: e.target.value }))}
                    className="w-full bg-[#2a2a2a] border border-white/10 rounded-lg px-4 py-2 text-white"
                    required
                  />
                </div>
                <div>
                  <label className="block text-gray-300 text-sm mb-1">Kapcsolattartó *</label>
                  <input
                    type="text"
                    value={formData.contactName}
                    onChange={e => setFormData(prev => ({ ...prev, contactName: e.target.value }))}
                    className="w-full bg-[#2a2a2a] border border-white/10 rounded-lg px-4 py-2 text-white"
                    required
                  />
                </div>
                <div>
                  <label className="block text-gray-300 text-sm mb-1">Email *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={e => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full bg-[#2a2a2a] border border-white/10 rounded-lg px-4 py-2 text-white"
                    required
                  />
                </div>
                <div>
                  <label className="block text-gray-300 text-sm mb-1">Telefon *</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={e => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                    className="w-full bg-[#2a2a2a] border border-white/10 rounded-lg px-4 py-2 text-white"
                    required
                  />
                </div>
              </div>

              {/* Structures */}
              <div>
                <label className="block text-gray-300 text-sm mb-2">Szerkezetek *</label>
                <div className="flex gap-3">
                  {structures.map(s => (
                    <label
                      key={s.id}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer transition-all ${
                        formData.structures.includes(s.id)
                          ? "border-secondary bg-secondary/10 text-white"
                          : "border-white/10 bg-[#2a2a2a] text-gray-400"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={formData.structures.includes(s.id)}
                        onChange={() => setFormData(prev => ({
                          ...prev,
                          structures: toggleArrayItem(prev.structures, s.id)
                        }))}
                        className="sr-only"
                      />
                      {s.name}
                    </label>
                  ))}
                </div>
              </div>

              {/* Regions */}
              <div>
                <label className="block text-gray-300 text-sm mb-2">Régiók *</label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 max-h-40 overflow-y-auto bg-[#2a2a2a] rounded-lg p-3">
                  {regions.map(r => (
                    <label
                      key={r}
                      className={`flex items-center gap-2 px-2 py-1 rounded cursor-pointer text-sm transition-all ${
                        formData.regions.includes(r) ? "text-secondary" : "text-gray-400"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={formData.regions.includes(r)}
                        onChange={() => setFormData(prev => ({
                          ...prev,
                          regions: toggleArrayItem(prev.regions, r)
                        }))}
                        className="accent-secondary"
                      />
                      {r}
                    </label>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-gray-300 text-sm mb-1">Kapacitás</label>
                  <select
                    value={formData.capacity}
                    onChange={e => setFormData(prev => ({ ...prev, capacity: e.target.value }))}
                    className="w-full bg-[#2a2a2a] border border-white/10 rounded-lg px-4 py-2 text-white"
                  >
                    <option value="low">Alacsony terheltség</option>
                    <option value="medium">Közepes terheltség</option>
                    <option value="high">Magas terheltség</option>
                  </select>
                </div>
                <div>
                  <label className="block text-gray-300 text-sm mb-1">Jutalék (%)</label>
                  <input
                    type="number"
                    value={formData.commissionRate}
                    onChange={e => setFormData(prev => ({ ...prev, commissionRate: parseInt(e.target.value) }))}
                    className="w-full bg-[#2a2a2a] border border-white/10 rounded-lg px-4 py-2 text-white"
                    min="0"
                    max="20"
                  />
                </div>
                <div>
                  <label className="block text-gray-300 text-sm mb-1">Státusz</label>
                  <select
                    value={formData.status}
                    onChange={e => setFormData(prev => ({ ...prev, status: e.target.value }))}
                    className="w-full bg-[#2a2a2a] border border-white/10 rounded-lg px-4 py-2 text-white"
                  >
                    <option value="active">Aktív</option>
                    <option value="inactive">Inaktív</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-gray-300 text-sm mb-1">Megjegyzések</label>
                <textarea
                  value={formData.notes}
                  onChange={e => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full bg-[#2a2a2a] border border-white/10 rounded-lg px-4 py-2 text-white resize-none"
                  rows={3}
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={resetForm}
                  className="flex-1 bg-white/10 hover:bg-white/20 text-white font-medium py-3 rounded-lg transition-all"
                >
                  Mégse
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-secondary hover:bg-hover text-white font-medium py-3 rounded-lg transition-all"
                >
                  {editingPartner ? "Mentés" : "Partner hozzáadása"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
